import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { groupingBasisOf, formatGroupCell } from 'utils/groupLabel';
import { invitationAccessText } from 'utils/clubFeatures';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

import routes from 'routes';
import {
  getMembers,
  sendInvitation,
  exportClubMembersToFile,
  bulkSetMemberParticipation,
  planSuspendMembers,
} from 'api/userApi';
import { exportAndDownloadFile } from 'utils/fileDownload';

import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import CustomConfirmModal from 'components/common/CustomConfirmModal';
import Tooltip from 'components/common/Tooltip';
import AddMemberModal from 'components/admin/AddMemberModal/AddMemberModal';
import MemberAthletesCell from 'components/admin/MemberAthletesCell';

import useBulkSelection from 'hooks/useBulkSelection';
import useTableSort from 'hooks/useTableSort';
import useSessionFilters from 'hooks/useSessionFilters';
import BulkActionBar from 'components/admin/BulkActionBar';
import RosterRemovePreviewModal from 'components/admin/RosterRemovePreviewModal';
import BulkOperationResultModal from 'components/admin/BulkOperationResultModal';

import styles from 'components/admin/bulkAdminTable.module.css';

// Owner is intentionally omitted — the role exists in the data model but isn't
// exposed in the admin UI (the AccountOwner is hidden from member lists per
// docs/role-and-deletion-design.html §4).
const ROLE_OPTIONS = [
  { value: 'all', label: 'All roles' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Official', label: 'Official' },
  { value: 'Member', label: 'Member' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const PORTAL_ACCESS_OPTIONS = [
  { value: 'all', label: 'All portal access' },
  { value: 'Accepted', label: 'Accepted' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Not invited', label: 'Not invited' },
];

const roleBadgeClass = (role) => {
  switch (role) {
    case 'Owner': return styles.badgeRoleOwner;
    case 'Admin': return styles.badgeRoleAdmin;
    case 'Official': return styles.badgeRoleOfficial;
    case 'Guest': return styles.badgeRoleGuest;
    case 'Member':
    default: return styles.badgeRoleMember;
  }
};

const getDisplayName = (m) => {
  const fn = m?.firstName?.trim();
  const ln = m?.lastName?.trim();
  if (fn && ln) return `${fn} ${ln}`;
  if (fn) return fn;
  if (ln) return ln;
  return m?.email || 'Unknown';
};

const ClubMembers = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const clubId = selectedClub?.clubID || '';
  const groupingBasis = groupingBasisOf(selectedClub);

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);

  // Filters — persisted in sessionStorage so they survive navigate-away-and-back.
  const [filters, setFilter] = useSessionFilters(
    `clubMembersFilters_${clubId}`,
    { searchText: '', role: 'all', portalAccess: 'all', status: 'all' }
  );
  const { searchText, role: roleFilter, portalAccess: portalAccessFilter, status: statusFilter } = filters;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [memberToInvite, setMemberToInvite] = useState(null);
  const [isResend, setIsResend] = useState(false);
  const [inviting, setInviting] = useState(false);

  // Bulk remove — change-preview model (§5.6): preview the cascade, confirm, apply.
  const [removeSeed, setRemoveSeed] = useState(null);

  // Bulk set-participation (§5.7): suspend uses plan → confirm → apply; activate a plain confirm.
  const [suspendPlan, setSuspendPlan] = useState(null);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkResultTitle, setBulkResultTitle] = useState('');

  const fetchMembers = useCallback(async () => {
    if (!clubId) return;
    try {
      setLoading(true);
      const response = await getMembers(clubId);
      const enriched = (response.data || []).map((m) => ({
        ...m,
        name: getDisplayName(m),
        // The athlete's group as the club reads it: "(11M)", or "(Y6M)" for a club that groups by year level.
        profiles: (m.athletes || [])
          .map((a) => `${a.firstName} ${a.lastName} (${formatGroupCell(groupingBasis, a.age)}${a.gender})`)
          .join(', '),
      }));
      setMembers(enriched);
    } catch (err) {
      console.error('[ClubMembers] fetch error:', err);
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [clubId, groupingBasis]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Family-aware search key: a secondary keys to its primary, everyone else to themselves — so a family's
  // searchable text is pooled and searching any member (primary, secondary, or athlete) surfaces the whole group.
  const familyKey = useCallback(
    (m) =>
      m.contactRole === 'SecondaryContact' && m.linkedPrimaryUserID
        ? m.linkedPrimaryUserID
        : m.userID,
    []
  );
  const familyHaystackByKey = useMemo(() => {
    const byKey = {};
    members.forEach((m) => {
      const text = `${m.name} ${m.email || ''} ${m.profiles || ''} ${m.linkedPrimaryName || ''}`.toLowerCase();
      byKey[familyKey(m)] = (byKey[familyKey(m)] || '') + ' ' + text;
    });
    return byKey;
  }, [members, familyKey]);

  // Filters. Role/status/portal apply per member; search applies family-wide (see familyHaystackByKey).
  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      if (roleFilter !== 'all' && m.role !== roleFilter) return false;
      if (portalAccessFilter !== 'all' && m.invitationStatus !== portalAccessFilter) return false;
      if (statusFilter !== 'all' && m.participationStatus !== statusFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const hay = familyHaystackByKey[familyKey(m)] || '';
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [members, roleFilter, portalAccessFilter, statusFilter, searchText, familyHaystackByKey, familyKey]);

  // Sort
  const sortAccessors = useMemo(
    () => ({
      // Sort by last name (then first) so the list orders by the primary contact's surname, not first name.
      name: (r) => `${(r.lastName || '').trim()} ${(r.firstName || '').trim()}`.toLowerCase(),
      email: (r) => r.email,
      role: (r) => r.role,
      profiles: (r) => r.profiles,
      participationStatus: (r) => r.participationStatus,
      invitationStatus: (r) => r.invitationStatus,
    }),
    []
  );
  const { sortedItems, sortKey, sortDir, setSort } = useTableSort(filteredMembers, {
    defaultKey: 'name',
    accessors: sortAccessors,
  });

  // Selection
  const idAccessor = useCallback((m) => m.userID, []);
  const selection = useBulkSelection(sortedItems, idAccessor);

  // Group families for nested rendering: a secondary whose primary is present in the list renders as a sub-row
  // under that primary; a secondary whose primary is NOT present (an elevated ex-secondary, or the primary is a
  // guest in the other list) renders standalone with a reference. See docs/secondary-contact-family-ui-plan.html §7.
  const presentUserIds = useMemo(
    () => new Set(sortedItems.map((m) => m.userID)),
    [sortedItems]
  );
  const isNestedChild = useCallback(
    (m) =>
      m.contactRole === 'SecondaryContact' &&
      !!m.linkedPrimaryUserID &&
      presentUserIds.has(m.linkedPrimaryUserID),
    [presentUserIds]
  );
  const childrenByPrimary = useMemo(() => {
    const map = {};
    sortedItems.forEach((m) => {
      if (isNestedChild(m)) {
        (map[m.linkedPrimaryUserID] = map[m.linkedPrimaryUserID] || []).push(m);
      }
    });
    return map;
  }, [sortedItems, isNestedChild]);
  const renderRows = useMemo(() => {
    const rows = [];
    sortedItems
      .filter((m) => !isNestedChild(m))
      .forEach((m) => {
        rows.push({ member: m, isChild: false });
        (childrenByPrimary[m.userID] || []).forEach((c) =>
          rows.push({ member: c, isChild: true })
        );
      });
    return rows;
  }, [sortedItems, childrenByPrimary, isNestedChild]);

  const handleInviteClick = (member, resend = false) => {
    if (member.participationStatus !== 'Active') {
      toast.error(
        'Cannot send invitation: Member status must be Active to receive invitations'
      );
      return;
    }
    setMemberToInvite(member);
    setIsResend(resend);
    setShowInviteModal(true);
  };

  const confirmInvitation = async () => {
    if (!memberToInvite) return;
    setInviting(true);
    try {
      await sendInvitation(clubId, memberToInvite.email);
      toast.success(
        isResend ? 'Invitation resent successfully' : 'Invitation sent successfully'
      );
      setShowInviteModal(false);
      setMemberToInvite(null);
      await fetchMembers();
    } catch (error) {
      const errorMessage =
        error?.response?.data || error?.message || 'Failed to send invitation';
      toast.error(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    fetchMembers();
  };

  // Exports the ticked rows, or the rows the filters leave showing when nothing is ticked.
  const handleExportToFile = async () => {
    const memberIds = selection.selectedOrVisibleIds;
    if (memberIds.length === 0) {
      toast.info('There are no members in the list to export.');
      return;
    }
    await exportAndDownloadFile(
      exportClubMembersToFile,
      [clubId, 'ClubMembersOnly', memberIds],
      '',
      'The club members list was successfully exported',
      'Fetching club members data from the server...'
    );
  };

  const memberNameById = useMemo(() => {
    const map = {};
    members.forEach((m) => {
      map[m.userID] = m.name || m.userID;
    });
    return map;
  }, [members]);

  const runBulkSetParticipation = async (status) => {
    setBulkLoading(true);
    try {
      const response = await bulkSetMemberParticipation(
        clubId,
        selection.selectedIds,
        status
      );
      setBulkResult(response.data);
      setBulkResultTitle(
        status === 'Active'
          ? 'Set Members Active — Results'
          : 'Suspend Members — Results'
      );
      selection.clear();
      await fetchMembers();
    } catch (err) {
      console.error('[ClubMembers] bulk set-participation error:', err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Bulk operation failed';
      toast.error(typeof msg === 'string' ? msg : 'Bulk operation failed');
    } finally {
      setBulkLoading(false);
      setConfirmActivate(false);
      setSuspendPlan(null);
    }
  };

  // Suspend is the change-preview model (§5.6/§5.7): fetch the dry-run plan, show it, confirm, apply.
  const openSuspendPreview = async () => {
    try {
      setLoading(true);
      const response = await planSuspendMembers(clubId, selection.selectedIds);
      setSuspendPlan(response.data);
    } catch (err) {
      console.error('[ClubMembers] suspend plan error:', err);
      toast.error('Failed to compute the suspension preview');
    } finally {
      setLoading(false);
    }
  };

  const renderSuspendPlan = (plan) => {
    const changing = (plan.members || []).filter((m) => m.willChange);
    const skipped = (plan.members || []).filter((m) => !m.willChange);
    const athletes = plan.athletes || [];
    const droppedRegs = plan.droppedRegistrations || [];
    const contacts = plan.contactsStoodDown || [];
    const sectionStyle = { marginTop: '0.75rem' };
    const listStyle = {
      margin: '0.25rem 0 0',
      paddingLeft: '1.25rem',
      maxHeight: 140,
      overflowY: 'auto',
    };
    return (
      <div>
        <p>
          Suspend <strong>{changing.length}</strong>{' '}
          {changing.length === 1 ? 'member' : 'members'}? They stay on the club
          roster and can be set active again when they return.
        </p>
        {skipped.length > 0 && (
          <div style={sectionStyle}>
            <strong>Skipped ({skipped.length}):</strong>
            <ul style={listStyle}>
              {skipped.map((m) => (
                <li key={m.userId}>
                  {m.name} — {m.skipReason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {athletes.length > 0 && (
          <div style={sectionStyle}>
            <strong>Athletes set inactive ({athletes.length}):</strong>
            <ul style={listStyle}>
              {athletes.map((a) => (
                <li key={a.athleteId}>
                  {a.name}
                  {a.membershipType !== 'Full' ? ` (${a.membershipType})` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {droppedRegs.length > 0 && (
          <div style={sectionStyle}>
            <strong>
              Upcoming meet registrations removed ({droppedRegs.length}):
            </strong>
            <ul style={listStyle}>
              {droppedRegs.map((r, i) => (
                <li key={i}>
                  {r.athleteName} — {r.meetName} (
                  {new Date(r.meetDate).toLocaleDateString()})
                </li>
              ))}
            </ul>
            <p
              style={{
                fontSize: '0.875rem',
                marginTop: '0.5rem',
                color: '#b91c1c',
              }}
            >
              Removed registrations are NOT restored on reactivation — returning
              athletes must be re-registered.
            </p>
          </div>
        )}
        {contacts.length > 0 && (
          <div style={sectionStyle}>
            <strong>
              Family contacts also set inactive ({contacts.length}):
            </strong>
            <ul style={listStyle}>
              {contacts.map((c) => (
                <li key={c.userId}>
                  {c.name}
                  {c.isSecondary ? ' (secondary contact)' : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Whether every selected member is already in the target state — short-circuits the flow client-side.
  const selectedSet = new Set(selection.selectedIds);
  const selectedMembers = members.filter((m) => selectedSet.has(m.userID));
  const allSelectedActive =
    selectedMembers.length > 0 &&
    selectedMembers.every((m) => m.participationStatus === 'Active');
  const allSelectedInactive =
    selectedMembers.length > 0 &&
    selectedMembers.every((m) => m.participationStatus === 'Inactive');

  const sortIndicator = (key) =>
    sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '';

  return (
    <>
      <MetaTags title="SplitFast | Members" />
      <AdminTablePageLayout loading={loading}>
        <PinProtectedAction clubId={clubId}>
          {({ requestPinValidation }) => {
            const triggerWithPin = (actionType, onApproved, description) =>
              requestPinValidation(actionType, () => onApproved(), null, description);

            const bulkActions = [
              {
                label: 'Set Active',
                variant: 'primary',
                tooltip:
                  'Restore the selected members to Active — their athletes are reactivated separately (Athletes page or import)',
                onClick: () => {
                  if (allSelectedActive) {
                    toast.info('All selected members are already active.');
                    return;
                  }
                  triggerWithPin(
                    'bulk-set-members-active',
                    () => setConfirmActivate(true),
                    'The Admin PIN is required to change member participation status. The selected members will be set to Active. Their athletes are not changed — reactivate returning athletes from the Athletes page or the athlete import.'
                  );
                },
              },
              {
                label: 'Set Inactive',
                variant: 'secondary',
                tooltip:
                  'Suspend the selected members — their athletes are set inactive and upcoming meet registrations are removed',
                onClick: () => {
                  if (allSelectedInactive) {
                    toast.info('All selected members are already inactive.');
                    return;
                  }
                  triggerWithPin(
                    'bulk-set-members-inactive',
                    openSuspendPreview,
                    "The Admin PIN is required to suspend members. Suspension keeps them on the club roster but sets them and ALL their athletes inactive, and removes the athletes' upcoming meet registrations. You will see exactly what will change before confirming. The club owner and yourself are always skipped."
                  );
                },
              },
              {
                label: 'Remove',
                variant: 'danger',
                tooltip:
                  'Remove from this club (portal accounts and memberships in other clubs are preserved)',
                onClick: () =>
                  triggerWithPin(
                    'bulk-remove-members',
                    () => setRemoveSeed({ userIds: selection.selectedIds }),
                    'The Admin PIN is required to remove members from this club. Their portal accounts and any memberships in other clubs are preserved. You will see exactly what will be removed — including any athletes whose only contact is being removed — before confirming. The club owner and yourself are always skipped.'
                  ),
              },
            ];

            return (
              <div className={styles.container}>
                <div className={styles.pageHeading}>
                  <div className={styles.pageTitle}>
                    Members
                    <span className={styles.countBadge}>
                      {members.length} {members.length === 1 ? 'Member' : 'Members'}
                    </span>
                  </div>
                  <div className={styles.pageActions}>
                    <CustomButton
                      onClick={handleExportToFile}
                      style={{
                        ...defaultButtonStyle,
                        marginBottom: 0,
                        fontSize: 14,
                        padding: '12px 19px 12px 27px',
                        height: 'unset',
                        backgroundColor: '#0ea5e9',
                      }}
                    >
                      Export to File
                    </CustomButton>
                    <CustomButton
                      onClick={() =>
                        triggerWithPin(
                          'add-member',
                          () => setShowAddModal(true),
                          'The Admin PIN is required to add a member to this club. This grants the person access to club content, and may create a new SplitFast account or link an existing one to your club.'
                        )
                      }
                      style={{
                        ...defaultButtonStyle,
                        marginBottom: 0,
                        fontSize: 14,
                        padding: '12px 19px 12px 27px',
                        height: 'unset',
                      }}
                    >
                      + Add Member
                    </CustomButton>
                  </div>
                </div>

                {/* Filter card */}
                <div className={styles.filterCard}>
                  <div className={`${styles.filterGroup} ${styles.filterGroupSearch}`}>
                    <label className={styles.filterLabel}>Search</label>
                    <input
                      className={styles.filterInput}
                      type="search"
                      placeholder="Search by name, email, or athlete…"
                      value={searchText}
                      onChange={(e) => setFilter('searchText', e.target.value)}
                    />
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Role</label>
                    <select
                      className={styles.filterSelect}
                      value={roleFilter}
                      onChange={(e) => setFilter('role', e.target.value)}
                    >
                      {ROLE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Portal Access</label>
                    <select
                      className={styles.filterSelect}
                      value={portalAccessFilter}
                      onChange={(e) => setFilter('portalAccess', e.target.value)}
                    >
                      {PORTAL_ACCESS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Status</label>
                    <select
                      className={styles.filterSelect}
                      value={statusFilter}
                      onChange={(e) => setFilter('status', e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterResultCount}>
                    Showing <strong>{filteredMembers.length}</strong> of {members.length}
                  </div>
                </div>

                <BulkActionBar
                  count={selection.count}
                  itemLabel="member"
                  actions={bulkActions}
                  onClear={selection.clear}
                  allSelected={selection.allSelected}
                  someSelected={selection.someSelected}
                  onToggleAll={selection.toggleAll}
                />

                <div className={styles.tableCard}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.dataTable}>
                      {/* Column widths: fixed-width for predictable cells
                          (checkbox, badges, actions); proportional for Name and
                          Athletes which carry variable-length text. */}
                      <colgroup>
                        <col style={{ width: '44px' }} />
                        <col style={{ width: '22%' }} />
                        <col style={{ width: '110px' }} />
                        <col />
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '130px' }} />
                        <col style={{ width: '170px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className={`${styles.checkboxCell} ${styles.notSortable}`}
                            aria-label="Select"
                          />

                          <th onClick={() => setSort('name')}>
                            Name / Email <span className={styles.sortIndicator}>{sortIndicator('name')}</span>
                          </th>
                          <th onClick={() => setSort('role')}>
                            Role <span className={styles.sortIndicator}>{sortIndicator('role')}</span>
                          </th>
                          <th onClick={() => setSort('profiles')}>
                            Athletes <span className={styles.sortIndicator}>{sortIndicator('profiles')}</span>
                          </th>
                          <th onClick={() => setSort('participationStatus')}>
                            Status <span className={styles.sortIndicator}>{sortIndicator('participationStatus')}</span>
                          </th>
                          <th onClick={() => setSort('invitationStatus')}>
                            Portal Access <span className={styles.sortIndicator}>{sortIndicator('invitationStatus')}</span>
                          </th>
                          <th className={styles.notSortable}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderRows.length === 0 ? (
                          <tr className={styles.emptyRow}>
                            <td colSpan={7}>
                              {members.length === 0
                                ? 'No members in this club.'
                                : 'No members match the current filters.'}
                            </td>
                          </tr>
                        ) : (
                          renderRows.map(({ member: m, isChild }) => {
                            const checked = selection.isSelected(m.userID);
                            const standaloneSecondary =
                              !isChild && m.contactRole === 'SecondaryContact';
                            // A family group = a primary with nested secondary contact(s), plus those secondary
                            // rows. They share one set of athletes, so we accent the group on the left and blank
                            // the secondary's Athletes cell (below) rather than repeating the list. A primary with
                            // no secondary gets no accent.
                            const hasSecondary =
                              !isChild && (childrenByPrimary[m.userID]?.length > 0);
                            const inFamily = isChild || hasSecondary;
                            const rowClass = [
                              checked ? styles.rowSelected : '',
                              inFamily ? styles.familyRow : '',
                            ]
                              .filter(Boolean)
                              .join(' ');
                            return (
                              <tr key={m.userID} className={rowClass}>
                                <td className={styles.checkboxCell}>
                                  <input
                                    type="checkbox"
                                    aria-label={`Select ${m.name}`}
                                    checked={checked}
                                    onChange={() => selection.toggle(m.userID)}
                                  />
                                </td>
                                <td>
                                  {isChild && (
                                    <span className={styles.muted} style={{ marginRight: 6 }}>└</span>
                                  )}
                                  {m.name}
                                  {isChild && (
                                    <span className={styles.muted} style={{ marginLeft: 6, fontSize: 12 }}>
                                      · Secondary
                                    </span>
                                  )}
                                  {standaloneSecondary && (
                                    <span className={styles.muted} style={{ marginLeft: 6, fontSize: 12 }}>
                                      · Secondary{m.linkedPrimaryName ? ` · ${m.linkedPrimaryName}` : ''}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <span className={`${styles.badge} ${roleBadgeClass(m.role)}`}>
                                    {m.role}
                                  </span>
                                </td>
                                <td>
                                  {isChild ? (
                                    <span className={styles.muted}>(same family)</span>
                                  ) : (
                                    <MemberAthletesCell athletes={m.athletes} nativeType="Full" />
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={`${styles.badge} ${m.participationStatus === 'Active' ? styles.badgeActive : styles.badgeInactive}`}
                                  >
                                    {m.participationStatus}
                                  </span>
                                </td>
                                <td>{m.invitationStatus}</td>
                                <td>
                                  <div className={styles.rowActions}>
                                    <Tooltip text="View member details">
                                      <Link
                                        to={routes.admin.members.showMember.url(clubId, m.email)}
                                        className={styles.iconLink}
                                      >
                                        View
                                      </Link>
                                    </Tooltip>
                                    {m.invitationStatus === 'Not invited' && (
                                      <Tooltip text="Send invitation email">
                                        <button
                                          className={`${styles.inlineBtn} ${styles.inlineBtnRed}`}
                                          onClick={() => handleInviteClick(m, false)}
                                        >
                                          Invite
                                        </button>
                                      </Tooltip>
                                    )}
                                    {m.invitationStatus === 'Pending' && (
                                      <Tooltip text="Resend invitation email">
                                        <button
                                          className={`${styles.inlineBtn} ${styles.inlineBtnWarning}`}
                                          onClick={() => handleInviteClick(m, true)}
                                        >
                                          Resend
                                        </button>
                                      </Tooltip>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          }}
        </PinProtectedAction>

        {showAddModal && (
          <AddMemberModal
            show={showAddModal}
            clubId={clubId}
            onClose={() => setShowAddModal(false)}
            onSuccess={handleAddSuccess}
          />
        )}

        {showInviteModal && (
          <CustomConfirmModal
            show={showInviteModal}
            title={isResend ? 'Resend Invitation' : 'Send Invitation'}
            content={
              <div>
                <p>
                  {isResend ? 'Resend invitation to ' : 'Send invitation to '}
                  <strong>{getDisplayName(memberToInvite)}</strong>?
                </p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.75rem', color: '#6b7280' }}>
                  {invitationAccessText(selectedClub)}
                </p>
              </div>
            }
            confirmLabel={isResend ? 'Resend Invitation' : 'Send Invitation'}
            loading={inviting}
            onConfirm={confirmInvitation}
            onCancel={() => {
              setShowInviteModal(false);
              setMemberToInvite(null);
            }}
          />
        )}

        {confirmActivate && (
          <CustomConfirmModal
            show={true}
            title="Set Members Active"
            content={`Set ${selection.count} ${selection.count === 1 ? 'member' : 'members'} to Active? Their athletes are not changed — reactivate returning athletes from the Athletes page or the athlete import.`}
            confirmLabel="Set Active"
            loading={bulkLoading}
            onConfirm={() => runBulkSetParticipation('Active')}
            onCancel={() => setConfirmActivate(false)}
          />
        )}

        {/* Suspend — change-preview (§5.7): dry-run plan, confirm, apply */}
        {suspendPlan && (
          <CustomConfirmModal
            show={true}
            title="Suspend Members"
            content={renderSuspendPlan(suspendPlan)}
            confirmLabel="Suspend"
            loading={bulkLoading}
            onConfirm={() => runBulkSetParticipation('Inactive')}
            onCancel={() => setSuspendPlan(null)}
          />
        )}

        <BulkOperationResultModal
          show={!!bulkResult}
          title={bulkResultTitle}
          result={bulkResult}
          itemLabel="member"
          nameLookup={(id) => memberNameById[id]}
          onClose={() => setBulkResult(null)}
        />

        {/* Bulk remove — change-preview (§5.6): preview the cascade, confirm, apply */}
        <RosterRemovePreviewModal
          show={!!removeSeed}
          clubId={clubId}
          seed={removeSeed}
          title="Remove Members"
          onClose={() => setRemoveSeed(null)}
          onApplied={() => {
            selection.clear();
            fetchMembers();
          }}
        />
      </AdminTablePageLayout>
    </>
  );
};

export default ClubMembers;
